import classNames from 'classnames';
import {
  type CSSProperties,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import styles from './plot-tooltip.module.css';
import { usePlotTooltipPortalEnabled } from './plot-tooltip-portal-context';

export type TooltipPosition = {
  x: number;
  y: number;
};

export type PlotTooltipPlacement = 'side' | 'vertical' | 'cursor';

type PlotTooltipSpace = 'chart' | 'viewport';

type PlotTooltipProps = {
  isVisible: boolean;
  children: ReactNode;
  placement?: PlotTooltipPlacement;
  space?: PlotTooltipSpace;
} & TooltipPosition;

const VERTICAL_GAP = 12;
const VIEWPORT_MARGIN = 8;
const ARROW_EDGE_INSET = 12;
const CURSOR_GAP_X = 12;
const CURSOR_GAP_Y = 16;

/**
 * Horizontal slide (in px) that keeps a centred vertical tooltip inside the
 * viewport; the arrow compensates by the same amount in CSS
 * (`--tooltip-shift-x`) so it keeps pointing at the anchor. Bounded so the
 * arrow stays on the body, clear of the rounded corners.
 */
const computeVerticalShiftX = (
  anchorViewportX: number,
  width: number
): number => {
  if (width <= 0) return 0;
  const desiredLeft = anchorViewportX - width / 2;
  const minLeft = VIEWPORT_MARGIN;
  const maxLeft = Math.max(
    minLeft,
    window.innerWidth - VIEWPORT_MARGIN - width
  );
  const shift = Math.min(Math.max(desiredLeft, minLeft), maxLeft) - desiredLeft;
  const maxShift = Math.max(0, width / 2 - ARROW_EDGE_INSET);
  return Math.min(Math.max(shift, -maxShift), maxShift);
};

/**
 * Two render modes:
 * - **Inline** (default): `position: absolute` inside the chart's DOM.
 * - **Portalled** (opt-in via `PlotTooltipPortalProvider`): `position: fixed`
 *   in `document.body` so the tooltip can escape ancestors that clip
 *   overflow — see the provider for the rationale.
 */
export const PlotTooltip = (props: PlotTooltipProps) => {
  const renderViaPortal = usePlotTooltipPortalEnabled();
  return renderViaPortal || props.space === 'viewport' ? (
    <PortalledPlotTooltip {...props} />
  ) : (
    <InlinePlotTooltip {...props} />
  );
};

const placementClass = (
  placement: PlotTooltipPlacement,
  flip: boolean,
  flipX: boolean
): (string | false | undefined)[] => {
  if (placement === 'cursor') {
    return [
      styles.tooltipCursor,
      flipX && styles.tooltipCursorFlipX,
      flip && styles.tooltipCursorFlipY,
    ];
  }
  if (placement === 'vertical') {
    return [flip ? styles.tooltipVerticalBottom : styles.tooltipVerticalTop];
  }
  return [flip && styles.tooltipLeft];
};

const InlinePlotTooltip = ({
  isVisible,
  x,
  y,
  children,
  placement = 'side',
}: PlotTooltipProps) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [flip, setFlip] = useState(false);
  const [flipX, setFlipX] = useState(false);
  const [shiftX, setShiftX] = useState(0);

  // `x` is chart-local; the flip decision is viewport-relative, so translate
  // through the chart container's screen rect.
  useLayoutEffect(() => {
    if (!isVisible) return;
    const chartRect = tooltipRef.current?.offsetParent?.getBoundingClientRect();
    const viewportX = chartRect ? chartRect.left + x : x;
    const viewportY = chartRect ? chartRect.top + y : y;
    if (placement === 'cursor') {
      const height = tooltipRef.current?.offsetHeight ?? 0;
      const width = tooltipRef.current?.offsetWidth ?? 0;
      setFlip(
        viewportY + CURSOR_GAP_Y + height > window.innerHeight - VIEWPORT_MARGIN
      );
      setFlipX(
        viewportX + CURSOR_GAP_X + width > window.innerWidth - VIEWPORT_MARGIN
      );
      setShiftX(0);
    } else if (placement === 'vertical') {
      const height = tooltipRef.current?.offsetHeight ?? 0;
      const width = tooltipRef.current?.offsetWidth ?? 0;
      setFlip(viewportY - VERTICAL_GAP - height < VIEWPORT_MARGIN);
      setFlipX(false);
      setShiftX(computeVerticalShiftX(viewportX, width));
    } else {
      setFlip(viewportX >= window.innerWidth / 2);
      setFlipX(false);
      setShiftX(0);
    }
  }, [isVisible, x, y, placement]);

  return (
    <div
      ref={tooltipRef}
      style={
        {
          display: isVisible ? 'block' : 'none',
          left: `${x}px`,
          top: `${y}px`,
          '--tooltip-shift-x': `${shiftX}px`,
        } as CSSProperties
      }
      className={classNames(
        styles.tooltip,
        styles.tooltipInline,
        placementClass(placement, flip, flipX)
      )}
      role="tooltip"
      data-testid="plot-tooltip"
    >
      {children}
    </div>
  );
};

type ViewportPlacement = {
  left: number;
  top: number;
  flip: boolean;
  flipX: boolean;
  shiftX: number;
};

const PortalledPlotTooltip = ({
  isVisible,
  x,
  y,
  children,
  placement = 'side',
  space = 'chart',
}: PlotTooltipProps) => {
  // The empty <span> is a position probe (not a visual element): its
  // `offsetParent` gives us the chart container's viewport rect, which we
  // need to translate the chart-local `x`/`y` into the viewport coords that
  // `position: fixed` requires.
  const anchorRef = useRef<HTMLSpanElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isViewportSpace = space === 'viewport';
  // document.body is the wrong host inside a modal <dialog>: the dialog sits in
  // the top layer, which is above every z-index, so a body-portaled tooltip
  // paints UNDER the backdrop and cannot be hovered. Land inside the dialog
  // when there is one — charts opened in a modal (the expand-chart dialog) hit
  // this, and they reach here via `space="viewport"` rather than the provider.
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);
  const [placementState, setPlacement] = useState<ViewportPlacement | null>(
    null
  );

  useLayoutEffect(() => {
    setPortalHost(anchorRef.current?.closest('dialog') ?? document.body);
  }, []);

  useLayoutEffect(() => {
    if (!isVisible) {
      setPlacement(null);
      return;
    }

    const recomputePlacement = () => {
      const chartRect = isViewportSpace
        ? undefined
        : anchorRef.current?.offsetParent?.getBoundingClientRect();
      const left = chartRect ? chartRect.left + x : x;
      const top = chartRect ? chartRect.top + y : y;
      if (placement === 'cursor') {
        const height = contentRef.current?.offsetHeight ?? 0;
        const contentWidth = contentRef.current?.offsetWidth ?? 0;
        setPlacement({
          left,
          top,
          flip:
            top + CURSOR_GAP_Y + height > window.innerHeight - VIEWPORT_MARGIN,
          flipX:
            left + CURSOR_GAP_X + contentWidth >
            window.innerWidth - VIEWPORT_MARGIN,
          shiftX: 0,
        });
      } else if (placement === 'vertical') {
        const height = contentRef.current?.offsetHeight ?? 0;
        const contentWidth = contentRef.current?.offsetWidth ?? 0;
        setPlacement({
          left,
          top,
          flip: top - VERTICAL_GAP - height < VIEWPORT_MARGIN,
          flipX: false,
          shiftX: computeVerticalShiftX(left, contentWidth),
        });
      } else {
        setPlacement({
          left,
          top,
          flip: left >= window.innerWidth / 2,
          flipX: false,
          shiftX: 0,
        });
      }
    };

    recomputePlacement();

    if (isViewportSpace) return;

    // `position: fixed` doesn't move with the page; recompute on scroll
    // and resize. Capture phase catches scrolls on inner containers
    // (e.g. the drawer body), not just window.
    window.addEventListener('scroll', recomputePlacement, true);
    window.addEventListener('resize', recomputePlacement);
    return () => {
      window.removeEventListener('scroll', recomputePlacement, true);
      window.removeEventListener('resize', recomputePlacement);
    };
  }, [isVisible, x, y, placement, isViewportSpace]);

  return (
    <>
      {/* Rendered in both modes. Chart-space placement reads its offsetParent;
          viewport-space does not, but still needs it to resolve the portal
          host below. Inert and zero-sized either way. */}
      <span
        ref={anchorRef}
        className={styles.tooltipAnchor}
        aria-hidden="true"
      />
      {isVisible
        ? createPortal(
            <div
              ref={contentRef}
              style={
                {
                  left: `${placementState?.left ?? x}px`,
                  top: `${placementState?.top ?? y}px`,
                  visibility: placementState ? 'visible' : 'hidden',
                  '--tooltip-shift-x': `${placementState?.shiftX ?? 0}px`,
                } as CSSProperties
              }
              className={classNames(
                styles.tooltip,
                styles.tooltipFixed,
                placementClass(
                  placement,
                  placementState?.flip ?? false,
                  placementState?.flipX ?? false
                )
              )}
              role="tooltip"
              data-testid="plot-tooltip"
            >
              {children}
            </div>,
            portalHost ?? document.body
          )
        : null}
    </>
  );
};
